/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * useModuleNavigation
 *
 * Determines which modules the current user can access and which is active.
 * SUPER_ADMIN sees all 5 modules (admin + 4 business modules).
 * Regular users see only their enabled modules (hardcoded to ['venta'] for now).
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { MODULE_REGISTRY, getModuleByPath, type ModuleDefinition } from '@/config/module-registry';

interface ModuleNavigation {
  availableModules: ModuleDefinition[];
  activeModule: ModuleDefinition | undefined;
  showSwitcher: boolean;
  isSuperAdmin: boolean;
}

export function useModuleNavigation(): ModuleNavigation {
  const { user } = useAuthStore();
  const location = useLocation();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const availableModules = useMemo(() => {
    if (isSuperAdmin) {
      return MODULE_REGISTRY.slice().sort((a, b) => a.order - b.order);
    }
    // TODO: read from user.enabledModules when backend adds it
    return MODULE_REGISTRY.filter((m) => m.id === 'venta');
  }, [isSuperAdmin]);

  const activeModule = useMemo(
    () => getModuleByPath(location.pathname),
    [location.pathname]
  );

  return {
    availableModules,
    activeModule,
    showSwitcher: availableModules.length > 1,
    isSuperAdmin,
  };
}
